/* eslint-disable @typescript-eslint/no-explicit-any */
import { useUploadMedia, useAddNoteTopic, useUpdateNote } from '@/hooks/api/dashboard'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import colors from '@/styles/colors'
import { getFileUniqueKey, getThumbImageURL, uploadMedia } from '@/utils/helper'
import { CheckCircle, Close } from '@mui/icons-material'
import { Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FileContent, FileErrors, SelectedFiles, useFilePicker } from 'use-file-picker'
import { toast } from 'react-hot-toast'
import { AddAlbumPreviewItem, GradientContainer } from '../__styled'
import { useLocation, useNavigate } from 'react-router-dom'
import SVG from 'react-inlinesvg'
import { Ego, Note } from '@/types'
import { CBox, RBox } from '@/pages/__styled'
import { SVGS } from '@/assets/svgs'
import { useDropzone, FileWithPath } from 'react-dropzone'
import { Constants } from '@/utils/constants'
import { TopicDetailsContext } from '@/context/TopicDetails'
import { RoundedActionButton } from '@/components/StyledComponents'

export default function AlbumInput() {
  const [files, setFiles] = useState<FileContent[]>([])
  const [pFiles, setPFiles] = useState<File[]>([])
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [noteToEdit, setNoteToEdit] = useState<Note | null>(null)
  const [editEgoObjects, setEditEgoObjects] = useState<Ego[]>([])
  const [ewUser] = useLocalStorage('ewUser', null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const query = useBrowserQuery()
  const topicID = query.get('topicID') || ''
  const noteID = query.get('noteID') || ''
  const status = query.get('status')
  const { topic } = useContext(TopicDetailsContext)
  const theme = useTheme()
  const isLessThanSmall = useMediaQuery(theme.breakpoints.down('md'))

  const uploadFile = useUploadMedia()
  const addNoteToTopic = useAddNoteTopic()
  const updateNote = useUpdateNote()
  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    const contents: FileContent[] = []

    let readCount = 0

    acceptedFiles.forEach((file) => {
      const reader = new FileReader()
      reader.onabort = () => {
        console.log('file reading has aborted')
        readCount += 1
      }
      reader.onerror = () => {
        readCount += 1
        console.log('file reading has failed')
      }
      reader.onload = () => {
        contents.push({
          ...file,
          content: reader.result as string,
          name: file.name,
          lastModified: file.lastModified,
        })
        readCount += 1
        if (readCount == acceptedFiles.length) {
          onFilesSelected({ plainFiles: acceptedFiles, filesContent: contents })
        }
      }
      reader.readAsDataURL(file)
    })
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  useEffect(() => {
    if (status === 'edit' && noteID?.length > 0) {
      // Find note and check if i am the owner.
      const note = topic.notes.find((e) => e.noteID == noteID && e.authorID == ewUser?.userID)
      if (note) {
        setNoteToEdit(note)
        setEditEgoObjects([...note.egoList])
      }
    }
  }, [status, noteID])

  useEffect(() => {
    if (addNoteToTopic.isSuccess) {
      query.set('noteID', addNoteToTopic.data.noteID)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    }
  }, [addNoteToTopic.isSuccess])

  const onFilesSelected = ({ plainFiles, filesContent }: SelectedFiles) => {
    const duplicated = plainFiles.some((i: File) =>
      pFiles.some((j: File) => getFileUniqueKey(i) === getFileUniqueKey(j)),
    )
    if (duplicated) {
      // To-Do: use toast message
      toast.error('You selected one or more duplicated file(s).')
      return
    } else if (files.length + filesContent.length > Constants.imagePickerLimit) {
      toast.error('You can upload 5 files at maximum.')
      return
    }
    if (filesContent.length > 0) {
      setFiles(files.concat(filesContent))
      setPFiles(pFiles.concat(plainFiles))
    }
  }

  const onFilePickError = (error: FileErrors) => {
    const { errors } = error
    errors.forEach((e) => {
      if (e.maxLimitExceeded) {
        toast.error('You can select 5 files at maximum.')
      } else if (e.fileSizeToolarge) {
        toast.error('The file size should be 5MB at maximum.')
      }
    })
  }

  const [openFileSelector, { loading }] = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: true,
    maxFileSize: 5,
    limitFilesConfig: { max: 5 },
    onFilesSuccessfulySelected: onFilesSelected,
    onFilesRejected: onFilePickError,
  })

  const onClickAdd = async () => {
    try {
      const egoItems: Ego[] = [...editEgoObjects]

      if (pFiles.length > 0) {
        setUploading(true)
      }

      for (let i = 0; i < pFiles.length; i++) {
        const result: any = await uploadMedia(pFiles[i])
        if (!result.isError) {
          const photo = await uploadFile.mutateAsync(result)
          egoItems.push({
            sequenceID: `${i}`,
            egoID: photo.egoID,
            photoFullURL: photo.egoImageURL,
            photoAlbumURL: photo.egoAlbumURL,
          })
          setUploadingCount(i + 1)
        }
      }

      if (noteToEdit) {
        updateNote
          .mutateAsync({
            topicID: topicID || '',
            noteID: noteID || '',
            noteText: 'placeholder',
            egoList: egoItems,
          })
          .then((value) => {
            clearEditMode()
            setUploadingCount(0)
            setUploading(false)
          })
      } else {
        addNoteToTopic
          .mutateAsync({
            topicID,
            egoItems,
            noteName: 'untitled',
            noteText: 'placeholder',
          })
          .then((value) => {
            setFiles([])
            setPFiles([])
            setUploadingCount(0)
            setUploading(false)
          })
      }
    } catch (err) {
      // To-Do: show toast message
      setUploading(false)
    }
  }

  const clearEditMode = () => {
    setNoteToEdit(null)
    setFiles([])
    setPFiles([])
    setEditEgoObjects([])

    query.delete('status')
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onRemoveEgoObject = (index: number) => {
    editEgoObjects.splice(index, 1)
    const updated = [...editEgoObjects]
    setEditEgoObjects(updated)

    if (files.length == 0 && updated.length == 0) {
      clearEditMode()
    }
  }

  const onRemoveFile = (index: number) => {
    files.splice(index, 1)
    const updatedFiles = [...files]
    pFiles.splice(index, 1)
    setFiles(updatedFiles)
    setPFiles([...pFiles])

    if (files.length == 0 && updatedFiles.length == 0 && noteToEdit != null) {
      clearEditMode()
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Upload Area */}
      <Box
        onClick={openFileSelector}
        sx={{
          padding: '48px 32px',
          border: isDragActive ? '2px dashed #6366f1' : '2px dashed rgba(226, 232, 240, 0.8)',
          borderRadius: '16px',
          backgroundColor: isDragActive ? 'rgba(99, 102, 241, 0.04)' : '#ffffff',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.02)',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.1)',
          },
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <RBox
          justifyContent={'center'}
          alignItems='center'
          flexDirection='column'
          gap={2}
          sx={{
            maxWidth: '500px',
            margin: '0 auto',
          }}
        >
          <RBox
            gap={'16px'}
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '16px',
                background:
                  'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <SVG src={SVGS.UploadFilledIcon} width={32} height={32} />
            </Box>
            <CBox gap={1}>
              <RBox gap={'6px'} justifyContent='center' alignItems='center' flexWrap='wrap'>
                <Typography
                  fontSize={17}
                  fontWeight={600}
                  color='#0f172a'
                  sx={{ letterSpacing: '-0.01em' }}
                >
                  {'Drag & Drop or'}
                </Typography>
                <Typography
                  fontSize={17}
                  fontWeight={700}
                  color='#6366f1'
                  sx={{ letterSpacing: '-0.01em' }}
                >{`Choose files`}</Typography>
                <Typography
                  fontSize={17}
                  fontWeight={600}
                  color='#0f172a'
                  sx={{ letterSpacing: '-0.01em' }}
                >
                  {'to upload'}
                </Typography>
              </RBox>
              <Typography
                color='#64748b'
                fontSize={13}
                fontWeight={500}
                sx={{ letterSpacing: '-0.01em', mt: 0.5 }}
              >
                Supported formats: jpg, png, gif, tiff (max 5MB each)
              </Typography>
            </CBox>
          </RBox>
        </RBox>
      </Box>

      {/* Selected Photos Section */}
      {(files?.length > 0 || editEgoObjects.length > 0) && (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            borderRadius: '16px',
            padding: '24px',
            position: 'relative',
          }}
        >
          {/* Section Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#0f172a',
                  letterSpacing: '-0.01em',
                  marginBottom: '4px',
                }}
              >
                Selected Photos
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#64748b',
                  letterSpacing: '-0.01em',
                }}
              >
                {editEgoObjects.length + files.length} photo
                {editEgoObjects.length + files.length === 1 ? '' : 's'} ready to{' '}
                {noteToEdit ? 'update' : 'add'}
              </Typography>
            </Box>
          </Box>

          {/* Photos Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '12px',
              marginBottom: 3,
            }}
          >
            {editEgoObjects.map((egoItem: Ego, index: number) => (
              <Box
                key={`${egoItem.egoID}`}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: '#6366f1',
                    '& .delete-button': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <img
                  src={getThumbImageURL(egoItem.photoAlbumURL)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  className='delete-button'
                  onClick={(event) => {
                    event.stopPropagation()
                    onRemoveEgoObject(index)
                  }}
                  sx={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: 32,
                    height: 32,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(8px)',
                    opacity: 0,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#ef4444',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <SVG src={SVGS.TrashIcon} color={'white'} width={16} height={16} />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    right: '6px',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.4)',
                  }}
                >
                  <CheckCircle sx={{ fontSize: '16px', color: '#ffffff' }} />
                </Box>
              </Box>
            ))}
            {files.map((file: FileContent, index: number) => (
              <Box
                key={`${index}-${file.name}`}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  border: '2px solid #6366f1',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    '& .delete-button': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <img
                  src={file.content}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  className='delete-button'
                  onClick={(event) => {
                    event.stopPropagation()
                    onRemoveFile(index)
                  }}
                  sx={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: 32,
                    height: 32,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(8px)',
                    opacity: 0,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#ef4444',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <SVG src={SVGS.TrashIcon} color={'white'} width={16} height={16} />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    left: '6px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    background: 'rgba(99, 102, 241, 0.9)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#ffffff',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    New
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Update Button - Bottom Right */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid rgba(226, 232, 240, 0.8)',
            }}
          >
            <Button
              variant='contained'
              onClick={(event) => {
                event.stopPropagation()
                onClickAdd()
              }}
              disabled={uploading}
              sx={{
                background: uploading
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                padding: '12px 32px',
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
                border: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: uploading
                    ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                    : 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)',
                  transform: uploading ? 'none' : 'translateY(-2px)',
                  boxShadow: uploading
                    ? '0 4px 16px rgba(99, 102, 241, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)'
                    : '0 8px 24px rgba(99, 102, 241, 0.4), 0 4px 8px rgba(0, 0, 0, 0.15)',
                },
                '&:active': {
                  transform: uploading ? 'none' : 'translateY(0)',
                },
                '&:disabled': {
                  cursor: 'not-allowed',
                  color: 'white',
                },
              }}
            >
              {uploading
                ? `Uploading... (${uploadingCount}/${files.length})`
                : noteToEdit
                  ? 'Update Photos'
                  : 'Add Photos'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
